import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Row, Col, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import FormContainer from "../components/FormContainer";
import GoogleIcon from "../components/GoogleIcon";
import Loader from "../components/Loader";
import { useLoginMutation } from "../slices/usersApiSlice";
import { setCredentials } from "../slices/authSlice";
import StayTop from "../components/StayTop";

const LoginScreen = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [login, { isLoading }] = useLoginMutation();
    const { userInfo } = useSelector((state) => state.auth);

    const { search } = useLocation();
    const sp = new URLSearchParams(search);
    const redirect = sp.get('redirect') || '/';

    // Show error if Google auth failed
    useEffect(() => {
        const googleError = sp.get('error');
        if (googleError === 'google_failed') {
            setError('Google sign in failed. Please try again or use email and password.');
        }
    }, []);

    useEffect(() => {
        if (userInfo) {
            const timer = setTimeout(() => {
                navigate(redirect);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [userInfo, redirect, navigate]);

    const submitHandler = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const res = await login({ email, password }).unwrap();
            dispatch(setCredentials({ ...res }));
            // No navigate here — useEffect handles it once userInfo is set
        } catch (err) {
            if (err?.status === 429) {
                setError('Too many login attempts. Please wait 15 minutes and try again.');
            } else if (err?.data?.message) {
                setError(err.data.message);
            } else if (err?.error) {
                setError(err.error);
            } else {
                setError('Unable to sign in. Please check your connection and try again.');
            }
        }
    };

    const handleGoogleLogin = () => {
        const baseUrl = process.env.NODE_ENV === 'development'
            ? 'http://localhost:5001'
            : 'https://retrodayz.co.uk';
        window.location.href = `${baseUrl}/api/users/auth/google`;
    };

    return (
        <FormContainer>
            <h1>Sign In</h1>
            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            <Form onSubmit={submitHandler} style={{ padding: '10px' }}>
                <Form.Group controlId="email" className="my-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                        type="email"
                        placeholder="Enter email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    />
                </Form.Group>

                <Form.Group controlId="password" className="my-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    />
                    <Row className="py-3">
                        <Col>
                            Forgot your password? <Link to="/passwordreset">Reset it</Link>
                        </Col>
                    </Row>
                </Form.Group>

                <Button
                    type="submit"
                    className="mt-2 blue-outline"
                    style={{ width: '100%', borderRadius: '50px' }}
                    disabled={isLoading}>
                    Sign In
                </Button>

                {isLoading && <Loader />}
            </Form>

            <div className="my-3 text-center text-muted">or</div>

            <Button
                onClick={handleGoogleLogin}
                style={{
                    width: '100%',
                    borderRadius: '50px',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    color: '#444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '10px',
                    fontWeight: '500',
                }}
            >
                <GoogleIcon />
                Continue with Google
            </Button>

            <Row className="py-3" style={{ padding: '10px' }}>
                <Col>
                    New Customer? <Link to={redirect ? `/register?redirect=${redirect}` : '/register'}>Register</Link>
                </Col>
            </Row>

            <Row className="py-2" style={{ padding: '10px' }}>
                <Col>
                    Didn't receive a verification email? <Link to="/resend-verification">Resend</Link>
                </Col>
            </Row>
            <StayTop />
        </FormContainer>
    );
};

export default LoginScreen;